import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtUSD } from "../lib/api";
import { Users, ChevronRight, AlertCircle } from "lucide-react";

/** Dashboard card that rolls up outstanding partner balances across every
 *  cost-share partner. Shows the total owed prominently plus the top 3
 *  partners with a balance, each linkable straight to their invoice list. */
export default function PartnerOutstandingCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/partners/outstanding").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || data.partners.length === 0) return null;
  const owed = data.partners.filter((p) => p.outstanding > 0.005);
  const total = data.totals.outstanding;
  if (owed.length === 0) return null;

  return (
    <div className="bg-white border-2 border-[#1F1E1B] mb-6" data-testid="partner-outstanding-card">
      <div className="bg-[#5C4A2B] text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Owed by Partners</span>
        </div>
        <Link to="/partner-invoices" data-testid="partner-outstanding-link" className="text-[10px] uppercase tracking-wider hover:underline flex items-center gap-1">
          All invoices <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="p-4">
        <div className="flex items-baseline gap-3 mb-3">
          <div className="font-display text-4xl font-black tabular text-[#BC4727]">{fmtUSD(total)}</div>
          <div className="text-xs text-[#66645E]">across {owed.length} partner{owed.length === 1 ? "" : "s"}</div>
        </div>
        <ul className="divide-y divide-[#E5E2D6]">
          {owed.slice(0, 4).map((p) => (
            <li key={p.partner_id} className="flex items-center justify-between py-2" data-testid={`partner-outstanding-row-${p.partner_id}`}>
              <div>
                <div className="font-semibold text-sm">{p.partner_name}</div>
                <div className="text-[10px] text-[#66645E] uppercase tracking-wider">
                  {p.unpaid_count} open · billed {fmtUSD(p.billed)}, paid {fmtUSD(p.paid)}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold tabular text-[#BC4727]">{fmtUSD(p.outstanding)}</div>
                <Link
                  to={`/partner-invoices?partner_id=${p.partner_id}`}
                  data-testid={`partner-outstanding-bill-${p.partner_id}`}
                  className="text-[10px] uppercase tracking-wider text-[#1A4135] hover:underline"
                >
                  Bill / view →
                </Link>
              </div>
            </li>
          ))}
        </ul>
        {owed.length > 4 && (
          <div className="text-[10px] text-[#66645E] uppercase tracking-wider mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {owed.length - 4} more with outstanding balances
          </div>
        )}
      </div>
    </div>
  );
}
